/**
 * Left rail for the New-product wizard (#272, UX-review finding 8) — the
 * TradeCore artifact's vertical "PRODUCT SETUP" stepper (progress %, numbered
 * steps with connector lines), restyled to the project's ink theme: the
 * active step is an ink pill, not the artifact's blue.
 *
 * Presentation only: the page owns the step state and the one react-hook-form
 * instance — every section stays mounted (visibility is CSS) so no field or
 * component state is lost between steps. Completed and visited steps are
 * clickable; unvisited ones are reached through Next, which validates the
 * current step's fields first.
 */
import { Check } from "lucide-react";
import { cn } from "@flint/ui";

export interface WizardStep {
  key: string;
  label: string;
  /** One-liner under the step title in the content column's header. */
  description: string;
}

export function ProductWizardStepper({
  steps,
  current,
  maxVisited,
  onGo,
}: {
  steps: readonly WizardStep[];
  current: number;
  maxVisited: number;
  onGo: (index: number) => void;
}) {
  const pct = Math.round(((current + 1) / steps.length) * 100);
  return (
    <nav
      aria-label="Product creation steps"
      className="rounded border border-line bg-paper px-4 py-4 shadow"
    >
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted">
          Product setup
        </span>
        <span className="font-mono text-xs font-semibold tabular-nums text-ink">{pct}%</span>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-line" aria-hidden>
        <div className="h-full rounded-full bg-ink" style={{ width: `${pct}%` }} />
      </div>

      <ol className="mt-4 flex flex-col">
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          const reachable = i <= maxVisited;
          return (
            <li key={s.key} className="relative">
              {/* Connector line to the next step, behind the number circle. */}
              {i < steps.length - 1 ? (
                <span
                  aria-hidden
                  className="absolute left-[19px] top-8 h-[calc(100%-16px)] w-px bg-line"
                />
              ) : null}
              <button
                type="button"
                disabled={!reachable}
                aria-current={active ? "step" : undefined}
                onClick={() => reachable && onGo(i)}
                className={cn(
                  "relative flex w-full items-center gap-2.5 rounded px-1.5 py-1.5 text-left text-sm transition-colors",
                  active
                    ? "bg-ink text-paper"
                    : reachable
                      ? "text-ink-2 hover:bg-bg"
                      : "text-muted/70 cursor-not-allowed",
                )}
              >
                <span
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold tabular-nums",
                    active
                      ? "border-paper/40 bg-paper/10"
                      : done
                        ? "border-ink bg-ink text-paper"
                        : "border-line-2 bg-paper",
                  )}
                  aria-hidden
                >
                  {done ? <Check size={12} strokeWidth={3} /> : i + 1}
                </span>
                <span className="truncate font-medium">{s.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
